import * as React from "react";
import { useState, useEffect } from "react";
import { T, useTranslateKey } from "../../hooks/useTranslate";
import { ActionableIndicator } from "../../components/ActionableIndicator";
import { useActionableItemsContext } from "../../hooks/ActionableItemsContext";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useMixPanel } from "../../hooks/useMixPanel";
import { Typography } from "@unicity/design-system";

import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack
} from "@mui/material";
const BusinessNavItem = ({
  item,
  open,
  isMobile,
  drawerOpen,
  toggleDrawer
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isActionable } = useActionableItemsContext();

  const [navState, setNavState] = useState(false);
  const translatedItemTitle = useTranslateKey(item.text);
  const { logEvent } = useMixPanel();

  const imgSrc = navState ? item.iconSrc.replace(".svg", "-selected.svg") : item.iconSrc;

  const handleNavClick = () => {
    if (navState) return; // prevent navigation when selected
    logEvent(item.eventName, { location: "menu" });
    navigate(item.url);
  }

  const handleMobileNavClick = () => {
    if (navState) return; // prevent navigation when selected
    logEvent(item.eventName, { location: "menu" });
    if (!navState) {
      toggleDrawer();
    }
    navigate(item.url);
  }

  useEffect(() => {
    // if the first part of the pathname matches the value of the nav item
    // set the nav item to selected
    if (location.pathname.split("/")[1] === item.value) {
      setNavState(true);
    } else {
      setNavState(false);
    }
    // eslint-disable-next-line
  }, [])

  if (isMobile) {
    return (
      <Box
        id={`${item.value}-nav-item`}
        data-test-id={`${item.value}-nav-item`}
        title={translatedItemTitle}
        sx={navState ? selectedBoxStyle : null}
      >
        <ListItem
          aria-label={item.text}
          value={item.value}
          onClick={handleMobileNavClick}
          disablePadding
          sx={{ display: "block" }}
        >
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: drawerOpen ? "initial" : "center",
              px: 1.2,
              pointerEvents: navState ? 'none' : 'auto',
              cursor: navState ? 'default' : 'pointer',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: drawerOpen ? 3 : "auto",
                justifyContent: "center",
              }}
            >
              <img src={imgSrc} alt={`${item.text} icon`} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: "flex", gap: "6.5px" }}>
                  <ActionableIndicator invisible={!isActionable(item.text)}>
                    <Stack direction="row" gap={.5} sx={{display: "inline-block"}}>
                      <Typography variant="caption" sx={{display: 'inline'}}>
                        <T>{item.text}</T>
                      </Typography>
                      {(item.tags?.includes("beta")) && (
                        <Stack
                          justifyContent="center"
                          sx={{                            
                            padding: "2px 6px",
                            marginLeft: 1,                            
                            backgroundColor: theme.beta.default.backgroundColor,
                            borderRadius: "3px",
                            display: "inline-block",
                          }}
                        >
                          <Typography 
                            variant="small"
                            sx={{
                              display: 'inline', 
                              color: `${theme.beta.default.color} !important`, 
                              fontSize: "calc(8px * var(--unicity-font-scale, 1))"
                            }}
                          >
                            <T>beta</T>
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </ActionableIndicator>
                </Box>
              }
              primaryTypographyProps={{
                fontFamily: "Inter",
                fontWeight: "600",
                fontSize: "calc(12px * var(--unicity-font-scale, 1))",                
              }}
              sx={{ opacity: drawerOpen ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    );
  }
  return (
    <ListItem
      id={`${item.value}-nav-item`}
      data-test-id={`${item.value}-nav-item`}
      title={translatedItemTitle}
      aria-label={item.text}
      onClick={handleNavClick}
      value={item.value}
      disablePadding
      sx={{ display: "block" }}
    >
      <ListItemButton
        sx={{
          minHeight: 48,
          justifyContent: open ? "initial" : "center",
          px: 2.5,
          mb: "10px",
          pointerEvents: navState ? 'none' : 'auto',
          cursor: navState ? 'default' : 'pointer',
        }}
        selected={navState}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 3 : "auto",
            justifyContent: "center",
          }}
        >
          <ActionableIndicator padRight={-6} invisible={!(isActionable(item.text) && !open)}>
            <img style={{ display: 'inline-block' }} src={imgSrc} alt={`${item.text} icon`} />
          </ActionableIndicator>          
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: "flex", gap: "6.5px" }}>
              <ActionableIndicator invisible={!isActionable(item.text)}>
                <Stack direction="row" gap={.5} sx={{display: "inline-block"}}>
                  <Typography variant="menuItem" sx={{display: 'inline'}}>
                    <T>{item.text}</T>
                  </Typography>
                  {(item.tags?.includes("beta")) && (
                    <Stack
                      justifyContent="center"
                      sx={{
                        fontSize: "calc(8px * var(--unicity-font-scale, 1))",
                        padding: "2px 6px",
                        marginLeft: 1,
                        backgroundColor: theme.beta.default.backgroundColor,
                        borderRadius: "3px",
                        display: "inline-block",
                      }}
                    >
                      <Typography 
                        variant="small"
                        sx={{
                          display: 'inline', 
                          color: `${theme.beta.default.color} !important`, 
                          fontSize: "calc(8px * var(--unicity-font-scale, 1))"
                        }}
                      >
                        <T>beta</T>
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </ActionableIndicator>
            </Box>
          }
          primaryTypographyProps={{
            fontFamily: "Inter",
            fontWeight: "600",
            fontSize: "12px",
          }}
          sx={{ opacity: open ? 1 : 0 }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const selectedBoxStyle = {
  backgroundColor: "rgba(0, 158, 207, 0.1)",
  borderRadius: "8px",
};

export { BusinessNavItem };
